<template>
  <div class="dropdown">
    <input type="text" class="form-control search-control" v-model="searchQuery" ref="input"
           @keydown.enter="enter"
           @keydown.down="down"
           @keydown.up="up"
           @focus="focus"
           @blur="blur"
    >
    <div class="dropdown-menu d-block w-100" v-if="(items.length > 0 && focused) || isLoading" @mousedown="mousedown">
      <div class="dropdown-item-text fa-2x" v-if="isLoading">
        <i class="fas fa-circle-notch fa-spin"></i>
      </div>
        <a class="dropdown-item"
           v-for="(item, index) in items"
           ref="results"
           :class="{'active text-white': index === current}"
           @click="selectOption(item)"
        >{{ item.name }}</a>
    </div>
  </div>
</template>

<script lang="ts">
import {SearchResultItem} from '@/model/api/search';
import * as Sentry from '@sentry/vue';
import {debounce, indexOf} from 'lodash-es';
import {defineComponent, nextTick, PropType, ref, watch} from 'vue';

export default defineComponent({
    props: {
        search: {
            type: Function as PropType<(searchQuery: string) => Promise<SearchResultItem[]>>,
            required: true,
        },
        value: {
            type: Object as PropType<SearchResultItem | null>,
            default: null,
        },
    },
    emits: ['input'],
    setup(props, {emit, expose}) {
        const input = ref<HTMLElement | null>(null);
        const searchQuery = ref('');
        const items = ref<SearchResultItem[]>([]);
        const isLoading = ref(false);
        const focused = ref(false);
        const current = ref(0);

        let preventSearch = false;

        function isEmpty(): boolean {
            return props.value === null;
        }

        function updateResults(): void {
            if (preventSearch) {
                preventSearch = false;
                return;
            }

            if (searchQuery.value === '') {
                return;
            }

            items.value = [];
            isLoading.value = true;

            props.search(searchQuery.value)
                .then((result) => {
                    items.value = result;
                    current.value = 0;
                })
                .catch((error) => {
                    Sentry.captureException(error);
                    items.value = [];
                })
                .finally(() => {
                    nextTick(() => isLoading.value = false);
                });
        }

        const lazySearch = debounce(updateResults, 1000);

        // created logic
        if (props.value) {
            preventSearch = true;
            searchQuery.value = props.value.name;
        }

        watch(searchQuery, () => {
            if (searchQuery.value === '') {
                emit('input', null);
                return;
            }

            lazySearch();
        });

        function selectOption(value: SearchResultItem): void {
            preventSearch = true;
            searchQuery.value = value.name;
            current.value = indexOf(items.value, value);
            input.value?.blur();

            emit('input', value);
        }

        function up(): void {
            current.value = current.value === 0 ? Object.keys(items.value).length : current.value - 1;
        }

        function down(): void {
            current.value = current.value === Object.keys(items.value).length ? 0 : current.value + 1;
        }

        function enter(): void {
            if (items.value.length === 0) {
                return;
            }

            selectOption(items.value[current.value]);
        }

        function focus(): void {
            focused.value = true;
        }

        function blur(): void {
            focused.value = false;
        }

        function mousedown(e: Event): void {
            e.preventDefault();
        }

        expose({isEmpty, updateResults});

        return {
            input, searchQuery, items, isLoading, focused, current,
            selectOption, up, down, enter, focus, blur, mousedown,
        };
    },
});
</script>
